import { FC, useEffect } from 'react'

import {
  TaxonomyCategory,
  TaxonomyNatureOption,
  TaxonomyServiceType,
  TaxonomySubcategory,
  TaxonomyTask,
} from './CaseTaxonomy.types'
import { TaxonomyNodeSelectField } from './TaxonomyNodeSelectField'
import { findSelectionByTaxonomyId, natureLabel, naturesForSelection } from './taxonomyTree'
import { useTaxonomyCascade } from './useTaxonomyCascade'
import { useTaxonomyTasks } from './useTaxonomyTasks'

interface TaxonomyClassificationFieldsProps {
  initialTaxonomyId?: string
}

const nodeToOption = <NodeType extends { label: string }>(nodes: NodeType[]) =>
  nodes.map(node => ({ label: node.label, value: node }))

export const TaxonomyClassificationFields: FC<TaxonomyClassificationFieldsProps> = ({
  initialTaxonomyId,
}) => {
  const { tasks, isLoading } = useTaxonomyTasks()
  const { isUserOverridden, applySelection, selections, handlers } = useTaxonomyCascade()

  // Edit hydration: map the saved taxonomyId back to the four cascade levels
  // once the tree is loaded. isUserOverridden distinguishes a pending
  // hydration from the user changing the pickers on purpose.
  const hasCascadeSelection = !!selections.task
  useEffect(() => {
    if (!initialTaxonomyId || !tasks.length || hasCascadeSelection || isUserOverridden) {
      return
    }
    const selection = findSelectionByTaxonomyId(tasks, initialTaxonomyId)
    if (selection) {
      applySelection(selection)
    }
  }, [initialTaxonomyId, tasks, hasCascadeSelection, isUserOverridden, applySelection])

  const categoryOptions = selections.serviceType?.categories ?? []
  const subcategoryOptions = selections.category?.subcategories ?? []
  const natureOptions = naturesForSelection({
    serviceType: selections.serviceType ?? undefined,
    category: selections.category ?? undefined,
    subcategory: selections.subcategory ?? undefined,
  })

  return (
    <>
      {/* Record type cannot change on update — same rule as the disabled
          legacy Record Type field. */}
      <TaxonomyNodeSelectField<TaxonomyTask>
        label="Record Type"
        formName="taxonomyTask"
        options={nodeToOption(tasks)}
        onNodeChange={handlers.handleTaskChange}
        getNodeLabel={task => task.label}
        isDisabled
      />
      <TaxonomyNodeSelectField<TaxonomyServiceType>
        label="Service Type"
        formName="taxonomyServiceType"
        options={nodeToOption(selections.task?.serviceTypes ?? [])}
        onNodeChange={handlers.handleServiceTypeChange}
        getNodeLabel={serviceType => serviceType.label}
        isDisabled={isLoading}
      />
      {categoryOptions.length > 0 ? (
        <TaxonomyNodeSelectField<TaxonomyCategory>
          label="Category"
          formName="taxonomyCategory"
          options={nodeToOption(categoryOptions)}
          onNodeChange={handlers.handleCategoryChange}
          getNodeLabel={category => category.label}
          isDisabled={isLoading}
        />
      ) : null}
      {subcategoryOptions.length > 0 ? (
        <TaxonomyNodeSelectField<TaxonomySubcategory>
          label="Subcategory"
          formName="taxonomySubcategory"
          options={nodeToOption(subcategoryOptions)}
          onNodeChange={handlers.handleSubcategoryChange}
          getNodeLabel={subcategory => subcategory.label}
          isDisabled={isLoading}
        />
      ) : null}
      {natureOptions.length > 1 ? (
        <TaxonomyNodeSelectField<TaxonomyNatureOption>
          label="Nature"
          formName="taxonomyNature"
          options={natureOptions.map(nature => ({ label: natureLabel(nature), value: nature }))}
          onNodeChange={handlers.handleNatureChange}
          getNodeLabel={natureLabel}
          isDisabled={isLoading}
        />
      ) : null}
    </>
  )
}
